/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { Info } from '@mui/icons-material';
import isNil from 'lodash/isNil';
import moment from 'moment';

import Terminal from '../../../../shared/entities/terminal';
import {
  daysUntilNow,
  durationFromDateUntilNowHoursMinutesSeconds,
  durationToNowInMin,
  MINUTES_IN_DAY,
} from '../../../../utils/DateTimeUtils';
import Tooltip from '../../../common/Tooltip';

export const LAST_ACTIVITY_REPORT_THRESHOLD_IN_DAYS = 90;
const NO_ACTIVITY_REPORTED = 'No activity reported';
const LAST_ACTIVITY_TOOLTIP_MAX_WIDTH_PX = 260;
const LAST_ACTIVITY_TOOLTIP_X_OFFSET_PX = 72;
const LAST_ACTIVITY_TOOLTIP_Y_OFFSET_PX = 44;

export interface OfflineTerminalDetailsLastReportedBannerState {
  now: Date;
  sourceTimeTooltipVisible: boolean;
}

export interface OfflineTerminalDetailsLastReportedBannerProps {
  readonly tailDetailsPanelTerminal: Terminal | undefined;
}

const OfflineTerminalDetailsLastReportedBanner = (props: OfflineTerminalDetailsLastReportedBannerProps) => {
  const { tailDetailsPanelTerminal } = props;

  const banner = (tailDetailsPanelTerminal: Terminal) => {
    if (!isNil(tailDetailsPanelTerminal.sourceTime)) {
      const prefix = 'Activity last reported';
      const suffix = 'ago';
      return (
        <span className="offline__values__text">
          {prefix} {getLastReportTimeOrDuration(tailDetailsPanelTerminal.sourceTime)} {suffix}
        </span>
      );
    } else {
      return <span className="offline__values__text">{NO_ACTIVITY_REPORTED}</span>;
    }
  };
  return tailDetailsPanelTerminal !== undefined ? (
    <Tooltip
      text={
        isNil(tailDetailsPanelTerminal.sourceTime)
          ? NO_ACTIVITY_REPORTED
          : `Last reported: ${moment.unix(tailDetailsPanelTerminal.sourceTime).utc().format('MM-DD-YYYY HH:mm:ss')} UTC`
      }
      tooltipMaxWidthPx={LAST_ACTIVITY_TOOLTIP_MAX_WIDTH_PX}
      xOffsetPx={LAST_ACTIVITY_TOOLTIP_X_OFFSET_PX}
      yOffsetPx={LAST_ACTIVITY_TOOLTIP_Y_OFFSET_PX}
    >
      <div className="offline__values__banner">
        <Info className="offline__values__banner__icon" />
        {banner(tailDetailsPanelTerminal)}
      </div>
    </Tooltip>
  ) : null;
};

export default OfflineTerminalDetailsLastReportedBanner;

// Takes in the sourceTime when the terminal last reported its activity and if the difference is less than 24 hours,
// the hours minutes seconds since is returned, else the UTC Timestamp of the last report is returned.
export const getLastReportTimeOrDuration = (sourceTime: number) => {
  const durationToNowInMinutes = durationToNowInMin(sourceTime);

  if (durationToNowInMinutes > MINUTES_IN_DAY * LAST_ACTIVITY_REPORT_THRESHOLD_IN_DAYS) {
    return `more than 90 days`;
  } else if (
    durationToNowInMinutes > MINUTES_IN_DAY &&
    durationToNowInMinutes <= MINUTES_IN_DAY * LAST_ACTIVITY_REPORT_THRESHOLD_IN_DAYS
  ) {
    // one day
    if (durationToNowInMinutes < MINUTES_IN_DAY * 2) {
      return `${Math.floor(daysUntilNow(sourceTime))} day`;
    }
    return `${Math.floor(daysUntilNow(sourceTime))} days`;
  } else {
    return durationFromDateUntilNowHoursMinutesSeconds(sourceTime);
  }
};
